// src/components/pos/POSOptions.js
import { useState } from 'react'
import { KARAT_OPTIONS } from './pos.constants'

function Ic({ path, size = 13, color = 'var(--text-muted)', sw = 1.8 }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round"><path d={path}/></svg>
}

function Section({ title, icon, defaultOpen = false, children, badge }) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div style={{ borderBottom:'1px solid var(--border)' }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 14px', background:'none', border:'none', cursor:'pointer', textAlign:'left' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'7px' }}>
          <Ic path={icon} size={13} color="var(--primary)" sw={2} />
          <span style={{ fontSize:'12px', fontWeight:700, color:'var(--text-primary)', textTransform:'uppercase', letterSpacing:'0.06em' }}>{title}</span>
          {badge && <span style={{ fontSize:'10px', fontWeight:700, padding:'1px 7px', borderRadius:'var(--radius-full)', background:'var(--primary)', color:'#fff' }}>{badge}</span>}
        </div>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" style={{ transform: open ? 'rotate(180deg)' : 'rotate(0)', transition:'transform 0.2s', flexShrink:0 }}><polyline points="6 9 12 15 18 9"/></svg>
      </button>
      {open && <div style={{ padding:'0 14px 12px' }}>{children}</div>}
    </div>
  )
}

function Toggle({ label, enabled, onToggle, summary }) {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 0', borderBottom:'1px solid var(--border)' }}>
      <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
        <button type="button" onClick={onToggle}
          style={{ width:'40px', height:'22px', borderRadius:'var(--radius-full)', background: enabled ? 'var(--primary)' : 'var(--border)', border:'none', cursor:'pointer', position:'relative', transition:'background 0.2s', flexShrink:0 }}>
          <div style={{ position:'absolute', top:'2px', width:'18px', height:'18px', borderRadius:'50%', background:'#fff', boxShadow:'0 1px 3px rgba(0,0,0,0.2)', transition:'left 0.2s', left: enabled ? '20px' : '2px' }} />
        </button>
        <span style={{ fontSize:'12.5px', fontWeight:600, color:'var(--text-primary)' }}>{label}</span>
      </div>
      {enabled && summary && <span style={{ fontSize:'11.5px', fontWeight:700, color:'var(--primary)' }}>{summary}</span>}
    </div>
  )
}

const inp = { width:'100%', border:'1px solid var(--border)', borderRadius:'var(--radius-md)', padding:'7px 10px', fontSize:'12px', outline:'none', background:'var(--bg-card)', color:'var(--text-primary)', fontFamily:'var(--font-ui)', boxSizing:'border-box', transition:'border-color 0.15s' }
const inpFocus = e => e.target.style.borderColor = 'var(--primary)'
const inpBlur  = e => e.target.style.borderColor = 'var(--border)'

export default function POSOptions({
  customer, setCustomer, options, setOptions,
  lookingUp, onPhoneLookup,
  heldOrders, onRestoreHeld, onDeleteHeld, onHoldCurrent,
  goldRates, isJewellery, posMode, sym,
  onOpenGoldRate,
  staffSession, seller,
}) {
  const setOpt  = (k, v) => setOptions(o => ({ ...o, [k]: v }))
  const setCust = (k, v) => setCustomer(c => ({ ...c, [k]: v }))

  const showTableFields    = posMode === 'fastfood'
  const showServiceFields  = ['repair', 'salon', 'service'].includes(posMode)
  const showOldGold        = isJewellery

  // Old gold buyback value calc
  const oldGoldBuybackRate = () => {
    if (!goldRates || !options.oldGoldKarat) return 0
    const k = options.oldGoldKarat
    if (k === 'Silver 925' || k === 'Silver 999') return goldRates.buyback_silver || 0
    const rateKey = `buyback_${k.toLowerCase().replace('k','').replace(' ','_')}k`
    return goldRates[rateKey] || 0
  }

  const computeOldGoldValue = () => {
    const rate = oldGoldBuybackRate()
    const weight = Number(options.oldGoldWeight) || 0
    return Math.round(rate * weight)
  }

  const handleOldGoldWeightChange = (w) => {
    setOpt('oldGoldWeight', w)
    const rate = oldGoldBuybackRate()
    setOpt('oldGoldValue', Math.round(rate * Number(w)))
  }

  return (
    <div style={{ display:'flex', flexDirection:'column' }}>

      {/* ── Gold rates card (jewellery mode) ── */}
      {isJewellery && (
        <div style={{ padding:'10px 14px', background:'#FFF8E6', borderBottom:'1px solid #FDE68A' }}>
          {goldRates ? (
            <div>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'6px' }}>
                <span style={{ fontSize:'11px', fontWeight:700, color:'var(--warning)', textTransform:'uppercase', letterSpacing:'0.06em' }}>Today's Gold Rates</span>
                <button onClick={onOpenGoldRate} style={{ fontSize:'11px', fontWeight:600, color:'var(--primary)', background:'none', border:'none', cursor:'pointer', padding:0 }}>Update</button>
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'4px' }}>
                {[
                  { label:'22K', sell: goldRates.rate_22k, buy: goldRates.buyback_22k },
                  { label:'18K', sell: goldRates.rate_18k, buy: goldRates.buyback_18k },
                  { label:'Silver', sell: goldRates.rate_silver, buy: goldRates.buyback_silver },
                ].filter(r => r.sell).map(r => (
                  <div key={r.label} style={{ background:'rgba(255,255,255,0.6)', borderRadius:'var(--radius-sm)', padding:'5px 7px' }}>
                    <div style={{ fontSize:'10px', fontWeight:700, color:'var(--warning)' }}>{r.label}</div>
                    <div style={{ fontSize:'11.5px', fontWeight:700, color:'var(--text-primary)' }}>{sym}{r.sell?.toLocaleString()}/g</div>
                    <div style={{ fontSize:'10px', color:'var(--text-muted)' }}>Buy: {sym}{r.buy?.toLocaleString() || '—'}/g</div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <button onClick={onOpenGoldRate}
              style={{ width:'100%', padding:'8px', borderRadius:'var(--radius-md)', border:'1px dashed var(--warning)', background:'transparent', fontSize:'12px', fontWeight:600, color:'var(--warning)', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'6px' }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="var(--warning)" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              Set today's gold rates
            </button>
          )}
        </div>
      )}

      {/* ── Customer ── */}
      <Section title="Customer" icon="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 3a4 4 0 100 8 4 4 0 000-8z" defaultOpen={true}>
        {/* Phone lookup */}
        <div style={{ display:'flex', gap:'6px', marginBottom:'8px' }}>
          <input value={customer.phone} onChange={e => { setCust('phone', e.target.value); setCust('lookedUp', false) }}
            placeholder="Phone number" style={{ ...inp, flex:1 }} onFocus={inpFocus} onBlur={inpBlur} />
          <button onClick={onPhoneLookup} disabled={lookingUp || !customer.phone}
            style={{ padding:'7px 10px', borderRadius:'var(--radius-md)', background:'var(--primary)', border:'none', fontSize:'11.5px', fontWeight:600, color:'#fff', cursor:'pointer', flexShrink:0, opacity: !customer.phone ? 0.5 : 1 }}>
            {lookingUp ? '...' : 'Find'}
          </button>
        </div>

        {/* Returning customer badge */}
        {customer.lookedUp && (
          <div style={{ padding:'6px 10px', borderRadius:'var(--radius-sm)', background:'#F0FDF4', border:'1px solid #BBF7D0', marginBottom:'8px', display:'flex', alignItems:'center', gap:'6px' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--success)" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
            <span style={{ fontSize:'11.5px', fontWeight:600, color:'var(--success)' }}>Returning customer</span>
            {customer.storeCredit > 0 && <span style={{ fontSize:'11px', color:'var(--warning)', marginLeft:'auto', fontWeight:600 }}>Credit: {sym}{customer.storeCredit}</span>}
          </div>
        )}

        <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
          <input value={customer.name} onChange={e => setCust('name', e.target.value)} placeholder="Name (optional)" style={inp} onFocus={inpFocus} onBlur={inpBlur} />
          <input value={customer.email} onChange={e => setCust('email', e.target.value)} placeholder="Email (optional)" style={inp} onFocus={inpFocus} onBlur={inpBlur} />
          {/* BIN for international sellers */}
          {seller?.seller_mode === 'international' && (
            <input value={customer.bin} onChange={e => setCust('bin', e.target.value)} placeholder="Client BIN (optional)" style={inp} onFocus={inpFocus} onBlur={inpBlur} />
          )}
        </div>
      </Section>

      {/* ── Old gold exchange (jewellery mode) ── */}
      {showOldGold && (
        <Section title="Old Gold Exchange" icon="M6 3h12l4 6-10 13L2 9l4-6z" defaultOpen={false} badge={options.oldGoldEnabled && options.oldGoldValue ? `−${sym}${Number(options.oldGoldValue).toLocaleString()}` : null}>
          <Toggle label="Accept old gold" enabled={options.oldGoldEnabled} onToggle={() => setOpt('oldGoldEnabled', !options.oldGoldEnabled)} />
          {options.oldGoldEnabled && (
            <div style={{ marginTop:'10px', display:'flex', flexDirection:'column', gap:'7px' }}>
              <div>
                <label style={{ fontSize:'11px', fontWeight:600, color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Karat</label>
                <select value={options.oldGoldKarat} onChange={e => { setOpt('oldGoldKarat', e.target.value); handleOldGoldWeightChange(options.oldGoldWeight) }}
                  style={inp} onFocus={inpFocus} onBlur={inpBlur}>
                  {KARAT_OPTIONS.map(k => <option key={k.key} value={k.key}>{k.label}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize:'11px', fontWeight:600, color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Weight (grams)</label>
                <input type="number" value={options.oldGoldWeight} onChange={e => handleOldGoldWeightChange(e.target.value)}
                  placeholder="0.00" step="0.01" style={inp} onFocus={inpFocus} onBlur={inpBlur} />
              </div>
              {/* Buyback rate info */}
              <div style={{ padding:'8px 10px', borderRadius:'var(--radius-sm)', background:'var(--bg-muted)', fontSize:'11.5px', color:'var(--text-muted)' }}>
                Buyback rate: <strong style={{ color:'var(--text-primary)' }}>{sym}{oldGoldBuybackRate().toLocaleString()}/g</strong>
                {options.oldGoldWeight && Number(options.oldGoldWeight) > 0 && (
                  <div style={{ marginTop:'4px', fontSize:'13px', fontWeight:700, color:'var(--warning)' }}>
                    Value: {sym}{computeOldGoldValue().toLocaleString()}
                  </div>
                )}
              </div>
              {/* Manual override */}
              <div>
                <label style={{ fontSize:'11px', fontWeight:600, color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Override value ({sym})</label>
                <input type="number" value={options.oldGoldValue} onChange={e => setOpt('oldGoldValue', e.target.value)}
                  placeholder="Auto-calculated" style={inp} onFocus={inpFocus} onBlur={inpBlur} />
              </div>
            </div>
          )}
        </Section>
      )}

      {/* ── Discount ── */}
      <Section title="Discount" icon="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" defaultOpen={false} badge={options.discountEnabled && options.discountPercent ? `-${options.discountPercent}%` : null}>
        <Toggle label="Apply discount" enabled={options.discountEnabled} onToggle={() => setOpt('discountEnabled', !options.discountEnabled)} />
        {options.discountEnabled && (
          <div style={{ marginTop:'10px', display:'flex', gap:'6px', alignItems:'center' }}>
            <input type="number" value={options.discountPercent} onChange={e => setOpt('discountPercent', e.target.value)}
              placeholder="0" min="0" max="100" style={{ ...inp, width:'70px', textAlign:'center', fontWeight:700 }} onFocus={inpFocus} onBlur={inpBlur} />
            <span style={{ fontSize:'13px', color:'var(--text-muted)' }}>%</span>
            {/* Quick presets */}
            {[5, 10, 15, 20].map(p => (
              <button key={p} onClick={() => setOpt('discountPercent', String(p))}
                style={{ padding:'4px 8px', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)', background: options.discountPercent === String(p) ? 'var(--primary)' : 'var(--bg-card)', color: options.discountPercent === String(p) ? '#fff' : 'var(--text-muted)', fontSize:'11px', fontWeight:600, cursor:'pointer' }}>
                {p}%
              </button>
            ))}
          </div>
        )}
      </Section>

      {/* ── Tax ── */}
      <Section title="Tax / VAT" icon="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8zM14 2v6h6" defaultOpen={false} badge={options.taxEnabled && options.taxPercent ? `+${options.taxPercent}%` : null}>
        <Toggle label="Apply tax" enabled={options.taxEnabled} onToggle={() => setOpt('taxEnabled', !options.taxEnabled)} />
        {options.taxEnabled && (
          <div style={{ marginTop:'10px', display:'grid', gridTemplateColumns:'1fr 70px', gap:'6px' }}>
            <select value={options.taxType} onChange={e => setOpt('taxType', e.target.value)} style={inp} onFocus={inpFocus} onBlur={inpBlur}>
              {['VAT','GST','Sales Tax','Service Charge','Other'].map(t => <option key={t}>{t}</option>)}
            </select>
            <input type="number" value={options.taxPercent} onChange={e => setOpt('taxPercent', e.target.value)}
              placeholder="%" style={{ ...inp, textAlign:'center', fontWeight:700 }} onFocus={inpFocus} onBlur={inpBlur} />
          </div>
        )}
      </Section>

      {/* ── Table / Food (fastfood mode) ── */}
      {showTableFields && (
        <Section title="Table / Cover" icon="M3 3h18v18H3zM3 9h18M3 15h18M9 9v12M15 9v12" defaultOpen={true}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'6px' }}>
            <div>
              <label style={{ fontSize:'11px', fontWeight:600, color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Table no.</label>
              <input value={options.tableNumber} onChange={e => setOpt('tableNumber', e.target.value)} placeholder="T1" style={inp} onFocus={inpFocus} onBlur={inpBlur} />
            </div>
            <div>
              <label style={{ fontSize:'11px', fontWeight:600, color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Covers</label>
              <input type="number" value={options.coverCount} onChange={e => setOpt('coverCount', e.target.value)} placeholder="0" style={inp} onFocus={inpFocus} onBlur={inpBlur} />
            </div>
          </div>
        </Section>
      )}

      {/* ── Service / Repair fields ── */}
      {showServiceFields && (
        <Section title="Service Details" icon="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" defaultOpen={true}>
          <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
            <div>
              <label style={{ fontSize:'11px', fontWeight:600, color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Job card number</label>
              <input value={options.jobCardNumber} onChange={e => setOpt('jobCardNumber', e.target.value)} placeholder="JC-001" style={inp} onFocus={inpFocus} onBlur={inpBlur} />
            </div>
            <div>
              <label style={{ fontSize:'11px', fontWeight:600, color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Technician / Staff</label>
              <input value={staffSession?.name || options.technicianName} readOnly={!!staffSession} onChange={e => setOpt('technicianName', e.target.value)} placeholder="Name" style={{ ...inp, background: staffSession ? 'var(--bg-muted)' : 'var(--bg-card)' }} onFocus={inpFocus} onBlur={inpBlur} />
            </div>
            <div>
              <label style={{ fontSize:'11px', fontWeight:600, color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Estimated ready date</label>
              <input type="date" value={options.estimatedReady} onChange={e => setOpt('estimatedReady', e.target.value)} style={inp} onFocus={inpFocus} onBlur={inpBlur} />
            </div>
            <div>
              <label style={{ fontSize:'11px', fontWeight:600, color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Advance paid</label>
              <input type="number" value={options.advancePaid} onChange={e => setOpt('advancePaid', e.target.value)} placeholder="0" style={inp} onFocus={inpFocus} onBlur={inpBlur} />
            </div>
          </div>
        </Section>
      )}

      {/* ── Branch / Staff / Note ── */}
      <Section title="Sale Details" icon="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" defaultOpen={false}>
        <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
          <div>
            <label style={{ fontSize:'11px', fontWeight:600, color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Branch</label>
            <input value={options.branchName} onChange={e => setOpt('branchName', e.target.value)} placeholder="Main branch" style={inp} onFocus={inpFocus} onBlur={inpBlur} />
          </div>
          {!staffSession && (
            <div>
              <label style={{ fontSize:'11px', fontWeight:600, color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Staff name</label>
              <input value={options.staffName} onChange={e => setOpt('staffName', e.target.value)} placeholder="Optional" style={inp} onFocus={inpFocus} onBlur={inpBlur} />
            </div>
          )}
          <div>
            <label style={{ fontSize:'11px', fontWeight:600, color:'var(--text-muted)', display:'block', marginBottom:'4px' }}>Note</label>
            <input value={options.note} onChange={e => setOpt('note', e.target.value)} placeholder="Optional note..." style={inp} onFocus={inpFocus} onBlur={inpBlur} />
          </div>
        </div>
      </Section>

      {/* ── Held orders ── */}
      <Section title="Held Orders" icon="M10 9V5l-7 7 7 7v-4.1c5 0 8.5 1.6 11 5.1-1-5-4-10-11-11z" defaultOpen={heldOrders.length > 0} badge={heldOrders.length > 0 ? String(heldOrders.length) : null}>
        {heldOrders.length === 0 ? (
          <div style={{ fontSize:'12px', color:'var(--text-muted)', textAlign:'center', padding:'8px 0' }}>No held orders</div>
        ) : heldOrders.map(h => (
          <div key={h.id} style={{ padding:'8px 10px', borderRadius:'var(--radius-md)', border:'1px solid var(--border)', background:'var(--bg-muted)', marginBottom:'6px' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'4px' }}>
              <span style={{ fontSize:'12.5px', fontWeight:600, color:'var(--text-primary)' }}>{h.label}</span>
              <span style={{ fontSize:'10.5px', color:'var(--text-muted)' }}>
                {new Date(h.created_at).toLocaleTimeString('en-BD', { hour:'2-digit', minute:'2-digit' })}
              </span>
            </div>
            <div style={{ fontSize:'11px', color:'var(--text-muted)', marginBottom:'6px' }}>
              {(() => { try { return JSON.parse(h.cart).length + ' item(s)' } catch { return '—' } })()}
              {h.held_by && ` · ${h.held_by}`}
            </div>
            <div style={{ display:'flex', gap:'5px' }}>
              <button onClick={() => onRestoreHeld(h)}
                style={{ flex:1, padding:'5px', borderRadius:'var(--radius-sm)', border:'none', background:'var(--primary)', color:'#fff', fontSize:'11px', fontWeight:700, cursor:'pointer' }}>
                Restore
              </button>
              <button onClick={() => onDeleteHeld(h.id)}
                style={{ padding:'5px 8px', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)', background:'var(--bg-card)', color:'var(--error)', fontSize:'11px', fontWeight:600, cursor:'pointer' }}>
                Delete
              </button>
            </div>
          </div>
        ))}
      </Section>
    </div>
  )
}